<script lang="ts">
  import { 
    LayoutDashboard, 
    Package, 
    Receipt, 
    Globe, 
    ChevronLeft, 
    ChevronRight, 
    LogOut,
    MessageSquare,
    Mail
  } from '@lucide/svelte';

  import { Badge, Button, Tooltip } from '@aatmanova/ui';

  interface Props {
    activeTab: string;
    currentUser: any;
    lowStockCount?: number;
    ordersCount?: number;
    quotesCount?: number;
    onlogout: () => void;
    drawerId?: string;
    theme?: string;
  }

  let {
    activeTab = $bindable(),
    currentUser,
    lowStockCount = 0,
    ordersCount = 0,
    quotesCount = 0,
    onlogout,
    drawerId,
    theme = 'dark'
  }: Props = $props();

  let isCollapsed = $state(false);

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'catalog', label: 'Catalog', icon: Package, badge: () => lowStockCount, badgeVariant: 'error' as const },
    { id: 'orders', label: 'Orders', icon: Receipt, badge: () => ordersCount, badgeVariant: 'warning' as const },
    { id: 'contacts', label: 'Contacts', icon: Mail, badge: () => 0, badgeVariant: 'info' as const },
    { id: 'quotes', label: 'Quotes', icon: MessageSquare, badge: () => quotesCount, badgeVariant: 'info' as const },
    { id: 'cms', label: 'Storefront CMS', icon: Globe }
  ];

  function closeDrawer() {
    if (drawerId && typeof document !== 'undefined') {
      const toggle = document.getElementById(drawerId) as HTMLInputElement;
      if (toggle) {
        toggle.checked = false;
      }
    }
  }
</script>

<!-- Collapsible Left Sidebar -->
<aside class="bg-base-100 border-r border-base-300 flex flex-col justify-between shrink-0 h-full w-64 lg:transition-all lg:duration-300 lg:ease-in-out relative overflow-visible lg:overflow-visible {isCollapsed ? 'lg:w-20' : 'lg:w-64'}">
  
  <!-- Collapse Toggle Handle Trigger (Floating premium button) -->
  <button 
    onclick={() => isCollapsed = !isCollapsed} 
    class="hidden lg:flex absolute -right-3.5 top-7 z-40 w-7 h-7 rounded-full border border-base-300 bg-base-100 hover:bg-base-200 hover:border-accent/50 hover:text-accent text-base-content/60 shadow-md transition-all duration-300 items-center justify-center cursor-pointer active:scale-90"
    aria-label="Toggle Sidebar"
  >
    {#if isCollapsed}
      <ChevronRight class="w-4 h-4 transition-transform duration-300" />
    {:else}
      <ChevronLeft class="w-4 h-4 transition-transform duration-300" />
    {/if}
  </button>

  <div class="p-4 space-y-8 overflow-visible">
    <!-- Brand Identity -->
    <div class="flex items-center min-h-[40px] transition-all duration-300 {isCollapsed ? 'justify-center px-0' : 'gap-2 px-2'}">
      {#if isCollapsed}
        <img
          src="/logo_original.png"
          alt="Aatmanova Icon"
          class="w-10 h-10 object-contain transition-transform duration-300 scale-95"
        />
      {:else}
        <img
          src={theme === 'dark' ? '/logo_light.png' : '/logo_dark.png'}
          alt="Aatmanova Logo"
          class="h-9 w-auto object-contain max-w-[160px] transition-all duration-300"
        />
      {/if}
    </div>

    <!-- Navigation Links -->
    <nav class="space-y-2">
      {#each navItems as item}
        {@const Icon = item.icon}
        {@const hasBadge = item.badge ? item.badge() > 0 : false}
        {@const badgeVal = item.badge ? item.badge() : 0}
        
        {#if isCollapsed}
          <!-- Collapsed Navigation Link -->
          <Tooltip tip={item.label} position="right" class="w-full hidden lg:block">
            <button
              onclick={() => { activeTab = item.id; closeDrawer(); }}
              class="w-full flex items-center justify-center p-3 rounded-xl text-sm font-semibold transition-all duration-300 relative group {activeTab === item.id ? 'bg-gradient-to-r from-accent/15 via-accent/5 to-transparent text-accent scale-105' : 'text-base-content/75 hover:bg-base-200 hover:text-base-content hover:scale-105'}"
            >
              {#if activeTab === item.id}
                <span class="absolute left-0 top-2.5 bottom-2.5 w-1 rounded-r-full bg-accent shadow-[0_0_8px_rgba(255,112,16,0.5)]"></span>
              {/if}
              <Icon class="w-5 h-5 shrink-0 transition-all duration-300 {activeTab === item.id ? 'text-accent scale-105' : 'group-hover:scale-110 group-hover:text-accent'}" />
              {#if hasBadge}
                <span class="absolute top-2.5 right-2.5 flex h-2.5 w-2.5">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 {item.badgeVariant === 'error' ? 'bg-error' : 'bg-warning'}"></span>
                  <span class="relative inline-flex rounded-full h-2.5 w-2.5 {item.badgeVariant === 'error' ? 'bg-error' : 'bg-warning'}"></span>
                </span>
              {/if}
            </button>
          </Tooltip>

          <!-- Mobile / Non-collapsed fallback drawer representation -->
          <button
            onclick={() => { activeTab = item.id; closeDrawer(); }}
            class="lg:hidden w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 relative group {activeTab === item.id ? 'bg-gradient-to-r from-accent/15 via-accent/5 to-transparent text-accent' : 'text-base-content/75 hover:bg-base-200 hover:text-base-content'}"
          >
            {#if activeTab === item.id}
              <span class="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-accent shadow-[0_0_8px_rgba(255,112,16,0.5)]"></span>
            {/if}
            <Icon class="w-5 h-5 shrink-0 transition-all duration-300 {activeTab === item.id ? 'text-accent scale-105' : 'group-hover:scale-110 group-hover:text-accent'}" />
            <span>{item.label}</span>
            {#if hasBadge}
              <Badge variant={item.badgeVariant} size="sm" class="ml-auto font-bold">{badgeVal}</Badge>
            {/if}
          </button>
        {:else}
          <!-- Expanded Navigation Link -->
          <button
            onclick={() => { activeTab = item.id; closeDrawer(); }}
            class="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all duration-300 relative group {activeTab === item.id ? 'bg-gradient-to-r from-accent/15 via-accent/5 to-transparent text-accent' : 'text-base-content/75 hover:bg-base-200 hover:text-base-content'}"
          >
            {#if activeTab === item.id}
              <span class="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-accent shadow-[0_0_8px_rgba(255,112,16,0.5)]"></span>
            {/if}
            <Icon class="w-5 h-5 shrink-0 transition-all duration-300 {activeTab === item.id ? 'text-accent scale-105' : 'group-hover:scale-110 group-hover:text-accent'}" />
            <span class="transition-colors">{item.label}</span>
            {#if hasBadge}
              <Badge variant={item.badgeVariant} size="sm" class="ml-auto font-bold">{badgeVal}</Badge>
            {/if}
          </button>
        {/if}
      {/each}
    </nav>
  </div>

  <!-- User Profile Section -->
  <div class="p-4 border-t border-base-300 bg-base-200/50 space-y-4">
    {#if !isCollapsed}
      <div class="flex items-center gap-3">
        <div class="avatar placeholder">
          <div class="bg-base-300 text-base-content border border-base-200 rounded-full w-10">
            <span class="text-sm font-bold">{currentUser?.name?.charAt(0).toUpperCase() || 'A'}</span>
          </div>
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-base-content truncate">{currentUser?.name || 'Admin'}</p>
          <p class="text-xs text-base-content/50 uppercase tracking-widest truncate">{currentUser?.role || 'ADMIN'}</p>
        </div>
      </div>
      <Button
        onclick={onlogout}
        variant="error"
        size="sm"
        class="w-full font-bold uppercase tracking-wider text-xs"
      >
        Sign Out
      </Button>
    {:else}
      <div class="flex flex-col items-center gap-3">
        <Tooltip tip="{currentUser?.name} ({currentUser?.role})" position="right" class="hidden lg:block">
          <div class="avatar placeholder">
            <div class="bg-base-300 text-base-content border border-base-200 rounded-full w-10">
              <span class="text-sm font-bold">{currentUser?.name?.charAt(0).toUpperCase() || 'A'}</span>
            </div>
          </div>
        </Tooltip>
        <div class="avatar placeholder lg:hidden">
          <div class="bg-base-300 text-base-content border border-base-200 rounded-full w-10">
            <span class="text-sm font-bold">{currentUser?.name?.charAt(0).toUpperCase() || 'A'}</span>
          </div>
        </div>
        <Button
          onclick={onlogout}
          variant="ghost"
          size="sm"
          class="btn-circle text-error hover:bg-error/10 flex items-center justify-center"
        >
          <LogOut class="w-5 h-5" />
        </Button>
      </div>
    {/if}
  </div>
</aside>
